<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import ConversationFrame from '@/components/chat/ConversationFrame.vue'
import MessageList from '@/components/chat/MessageList.vue'
import ChatComposer from '@/components/chat/ChatComposer.vue'
import AvatarZoomOverlay from '@/components/chat/AvatarZoomOverlay.vue'
import MessageSaveBadge from '@/features/story-conversation/components/MessageSaveBadge.vue'
import NotificationBanner from '@/components/ui/NotificationBanner.vue'
import { getActiveVariant, type MessageSlot } from '@/lib/messageSlots'
import type { AvatarZoomPayload } from '@/components/chat/avatarZoom'
import ConversationSettingsMenu from '@/features/story-conversation/components/ConversationSettingsMenu.vue'
import { useConversationAppearance } from '@/features/story-conversation/useConversationAppearance'
import { storyMessageFeatures } from '@/features/story-conversation/surface'
import type { StoryConversationPresentation, StoryMessageSave, StorySaveFeedback } from '@/features/story-conversation/surface'

const props = defineProps<{ presentation: StoryConversationPresentation }>()
const { appearanceStyles, fontFamily } = useConversationAppearance()
const avatarZoom = ref<AvatarZoomPayload | null>(null)
const avatarZoomOpen = ref(false)
const saveFeedback = ref<(StorySaveFeedback & { id: number }) | null>(null)
const retryingSave = ref(false)
let feedbackId = 0
let retryGeneration = 0
watch(() => props.presentation.key, () => {
  retryGeneration++
  saveFeedback.value = null
  retryingSave.value = false
}, { flush: 'sync' })
watch(() => props.presentation.key, () => { avatarZoom.value = null; avatarZoomOpen.value = false }, { flush: 'sync' })
const actionsBlocked = computed(() => !props.presentation.active || props.presentation.streaming || props.presentation.readOnly)
const styles = computed(() => {
  const name = props.presentation.accentName
  if (!name) return appearanceStyles.value
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return { ...appearanceStyles.value, '--color-character-accent': `hsl(${Math.abs(hash) % 360}, 50%, 55%)` }
})
function featuresForSlot(slot: MessageSlot) {
  return storyMessageFeatures(props.presentation.commands, {
    ...props.presentation.timeline.features, ...props.presentation.timeline.featuresForSlot?.(slot),
  })
}
function change(action: () => unknown) {
  if (!actionsBlocked.value) return action()
}
function send() {
  const { active, streaming, composer, commands } = props.presentation
  if (active && !streaming && !composer.disabled && composer.canSend) return commands.send()
}
function rewrite() {
  const { active, streaming, composer, commands } = props.presentation
  if (active && !streaming && !composer.disabled && composer.canRewrite) return commands.rewrite?.()
}
function zoom(payload: AvatarZoomPayload) { avatarZoom.value = payload; avatarZoomOpen.value = true }
async function retrySave(save: StoryMessageSave) {
  if (!props.presentation.active || props.presentation.streaming || save.busy || retryingSave.value) return
  const generation = retryGeneration
  retryingSave.value = true
  saveFeedback.value = null
  try {
    const feedback = await save.retry()
    if (generation === retryGeneration && props.presentation.active && feedback) {
      saveFeedback.value = { ...feedback, id: ++feedbackId }
    }
  } catch {
    if (generation === retryGeneration && props.presentation.active) {
      saveFeedback.value = { variant: 'error', message: 'The save could not be confirmed. Please try again.', id: ++feedbackId }
    }
  } finally {
    if (generation === retryGeneration) retryingSave.value = false
  }
}
</script>

<template>
  <ConversationFrame :style="styles" :font-family="fontFamily">
    <slot />
    <NotificationBanner v-if="saveFeedback" :key="saveFeedback.id" :message="saveFeedback.message"
      :variant="saveFeedback.variant" position="bottom" auto-dismiss @dismiss="saveFeedback = null" />
    <Teleport v-if="presentation.active && (presentation.menu || $slots['identity-tools'])" to="#app-chrome-tools" defer>
      <slot name="identity-tools" />
      <ConversationSettingsMenu :key="presentation.key" v-if="presentation.menu" v-bind="presentation.menu" :disabled="presentation.streaming || presentation.menu.disabled" v-slot="{ close }">
        <slot name="menu" :close="close" />
      </ConversationSettingsMenu>
    </Teleport>
    <template v-if="presentation.active" #timeline>
      <MessageList class="flex-1 min-h-0" v-bind="presentation.timeline"
        :features="storyMessageFeatures(presentation.commands, presentation.timeline.features)" :features-for-slot="featuresForSlot"
        :read-only="Boolean(actionsBlocked)" :actions-disabled="presentation.streaming"
        @edit="(id, content) => change(() => presentation.commands.edit?.(id, content))"
        @delete="id => change(() => presentation.commands.delete?.(id))"
        @fork="id => change(() => presentation.commands.fork?.(id))"
        @reroll="id => change(() => presentation.commands.reroll?.(id))"
        @resend="id => change(() => presentation.commands.resend?.(id))"
        @variant-select="(id, variant) => change(() => presentation.commands.selectVariant?.(id, variant))"
        @director-note:update="(id, note) => change(() => presentation.commands.updateDirectorNote?.(id, note))"
        @director-note:delete="id => change(() => presentation.commands.deleteDirectorNote?.(id))"
        @avatar-zoom="zoom">
        <template v-if="$slots['timeline-entry']" #timeline-entry="scope"><slot name="timeline-entry" v-bind="scope" :actions-disabled="presentation.streaming" /></template>
        <template v-if="$slots['timeline-before']" #before><slot name="timeline-before" /></template>
        <template #message-status="scope">
          <MessageSaveBadge v-if="presentation.saveForSlot?.(scope.messageSlot)" :key="String(scope.messageSlot.activeVariantId)"
            :save="{ ...presentation.saveForSlot(scope.messageSlot)!, busy: Boolean(retryingSave || presentation.saveForSlot(scope.messageSlot)?.busy) }"
            :content="getActiveVariant(scope.messageSlot).content" :copy-label="scope.messageSlot.is_user_author ? 'Copy message' : 'Copy response'"
            :disabled="presentation.streaming || !presentation.active" @retry="retrySave(presentation.saveForSlot(scope.messageSlot)!)" />
          <slot name="message-status" v-bind="scope" :actions-disabled="presentation.streaming" />
        </template>
        <template v-if="$slots['message-actions'] && !presentation.streaming" #message-actions="scope"><slot name="message-actions" v-bind="scope" :actions-disabled="presentation.streaming" /></template>
      </MessageList>
    </template>
    <template v-if="presentation.active" #composer>
      <slot name="composer-notices" />
      <ChatComposer :key="presentation.key" v-bind="presentation.composer" :is-streaming="presentation.streaming"
        @update:state="presentation.commands.updateComposer"
        @send="send" @stop="presentation.commands.stop"
        @rewrite="rewrite" @restore-original="presentation.commands.restoreOriginal?.()" />
    </template>
    <AvatarZoomOverlay :open="avatarZoomOpen" :src="avatarZoom?.src ?? null" :alt="avatarZoom?.alt ?? ''"
      :image-focus="avatarZoom?.focus ?? null" :source-el="avatarZoom?.el ?? null"
      @close="avatarZoomOpen = false" @closed="avatarZoom = null" />
  </ConversationFrame>
</template>
